/**
 * Die Anmeldung des Service Workers -- und zwar die **einzige** (#13).
 *
 * **Warum das an einer Stelle stehen muss.** Ein Bereich hat genau eine
 * Registrierung. Meldet die eine Stelle `/sw.js` an und die andere
 * `/sw.js?build=abc`, dann ist das keine zweite Registrierung, sondern
 * dieselbe -- die beiden Stellen überschrieben sich abwechselnd, und der
 * Browser installierte bei jedem Start einen „neuen" Worker. Vorher stand die
 * Anmeldung zweimal in `push.ts`; seit die Hülle zwischengespeichert wird,
 * wäre das eine Update-Schleife geworden.
 *
 * **Und warum der Stempel in der Adresse steht.** `sw.js` wird nicht gebündelt
 * -- sie liegt als fertige Datei im Release und kennt `__BUILD__` nicht. Über
 * die Adresse bekommt sie ihn trotzdem: der Worker liest ihn aus
 * `self.location` und benennt seinen Zwischenspeicher danach. Nebenbei ist die
 * geänderte Adresse selbst der Auslöser -- für den Browser ist ein anderes
 * Skript ein neuer Worker, und er installiert ihn.
 *
 * **`updateViaCache: 'none'`**, obwohl die `.htaccess` für `sw.js` schon
 * `no-cache` setzt. Zwei Sperren, aus demselben Grund wie dort das Paar aus
 * `mod_expires` und `mod_headers`: der Worker ist das Stück, das eine alte
 * Version am hartnäckigsten festhalten könnte.
 */

import { WORTLAUT } from '../texte'

let angemeldet: Promise<ServiceWorkerRegistration> | null = null

/**
 * Den Service Worker anmelden -- einmal je Sitzung, für jedes Gerät.
 *
 * Wirft, wenn der Browser keine Service Worker kann oder die Anmeldung
 * scheitert. Wer nur die Hülle will, darf das schlucken; wer Push einrichtet,
 * braucht die Registrierung und muss den Fehler sehen.
 */
export function huelleAnmelden(): Promise<ServiceWorkerRegistration> {
  if (angemeldet !== null) return angemeldet

  if (!('serviceWorker' in navigator)) {
    return Promise.reject(new Error(WORTLAUT.technik.fehler.keinServiceWorker))
  }

  angemeldet = navigator.serviceWorker
    .register(`/sw.js?build=${__BUILD__}`, { updateViaCache: 'none' })
    .catch((fehler: unknown) => {
      // Nicht merken, was fehlgeschlagen ist -- sonst scheitert der Knopf in
      // den Einstellungen später mit dem alten Fehler, obwohl es inzwischen
      // ginge.
      angemeldet = null
      throw fehler
    })

  return angemeldet
}

/**
 * Ask the service worker to take the **next** navigation from the net
 * (1.79.0).
 *
 * The worker hands out the stored shell at once, so that a start in a dead
 * spot is not a white screen. After a release that stored shell is the old
 * one; `StandWache` notices it at `/api/health` and reloads -- and this
 * marker makes that one reload fetch the new shell instead of getting the
 * old one again. The name has to match `NETZ_ZUERST` in `public/sw.js`, and
 * the store `ZIELSPEICHER` there, which survives the worker's activation.
 *
 * Never throws: without a store (private window, no worker) the reload goes
 * to the net anyway, because then nothing stands in between.
 */
export async function naechsterStartAusDemNetz(): Promise<void> {
  try {
    if (!('caches' in window)) return
    const speicher = await caches.open('tb-ziel')
    await speicher.put('/__tb-netz-zuerst', new Response('1'))
  } catch {
    // See above: then there is nothing to go around.
  }
}
